import React, { useEffect, useState } from 'react';
import { Calendar, X } from 'lucide-react';
import Button from '../../components/ui/Button';
import GrantPipelineHealthCard, { ApplicationStats, PIPELINE_ORDER } from './report/GrantPipelineHealthCard';
import { getAIReport, getCompliance, getDeadlines, getFunding, getPipeline, getTeam } from '../../api/report';
import { getDashboardAiInsight } from '../../api/dashboard';
import toast from 'react-hot-toast';

interface AppliedFilters {
  filterType?: string;
  startDate?: string;
  endDate?: string;
  rangeFilter?: string;
}

type AiInsightRaw = { insight?: string; metric?: string; value?: any } | string;

interface AiInsight {
  greeting?: string;
  insights?: AiInsightRaw[];
  next_steps?: string[];
  positive_message?: string;
}

interface NormalisedInsight {
  label?: string;
  text: string;
}

interface TeamMember {
  name: string;
  totalApplications: number;
}

interface Team {
  teamId: number;
  teamName: string;
  appsPerMember: TeamMember[];
}


function extractInsightText(raw: string): string {
  const insightKey = raw.indexOf('insight');
  if (insightKey !== -1) {
    const colonPos = raw.indexOf(':', insightKey);
    if (colonPos !== -1) {
      let valueStart = colonPos + 1;
      while (valueStart < raw.length && (raw[valueStart] === ' ' || raw[valueStart] === '\t')) valueStart++;
      const openQuote = raw[valueStart];
      if (openQuote === "'" || openQuote === '"') {
        let valueEnd = valueStart + 1;
        while (valueEnd < raw.length) {
          if (raw[valueEnd] === openQuote && raw[valueEnd - 1] !== '\\') break;
          valueEnd++;
        }
        const extracted = raw.slice(valueStart + 1, valueEnd).trim();
        if (extracted) return extracted;
      }
    }
  }
  return raw.trim();
}

function normaliseInsights(raw: AiInsightRaw[] | undefined): NormalisedInsight[] {
  if (!Array.isArray(raw)) return [];
  return raw
    .map((item): NormalisedInsight | null => {
      if (typeof item === 'string') {
        const trimmed = item.trim();
        if (!trimmed) return null;
        const text = extractInsightText(trimmed);
        return text ? { text } : null;
      }
      if (item && typeof item === 'object') {
        const text = String(item.insight ?? '').trim();
        const label = item.metric ? String(item.metric).trim() : undefined;
        if (!text && !label) return null;
        return { label, text: text || label || '' };
      }
      return null;
    })
    .filter((x): x is NormalisedInsight => x !== null);
}

function normaliseTeams(raw: any): Team[] {
  if (!raw) return [];

  if (Array.isArray(raw)) {
    return raw.map((t: any) => ({
      teamId:       t.teamId   ?? t.id   ?? 0,
      teamName:     t.teamName ?? t.name ?? `Team ${t.teamId ?? ''}`,
      appsPerMember: Array.isArray(t.appsPerMember)
        ? t.appsPerMember.map((m: any) => ({
            name:              m.name ?? m.member ?? 'Unknown',
            totalApplications: m.totalApplications ?? m.count ?? 0,
          }))
        : [],
    }));
  }

  if (raw.appsPerMember) {
    return [{
      teamId:   0,
      teamName: 'Team',
      appsPerMember: raw.appsPerMember.map((m: any) => ({
        name:              m.name ?? 'Unknown',
        totalApplications: m.count ?? m.totalApplications ?? 0,
      })),
    }];
  }

  if (raw.teams) return normaliseTeams(raw.teams);
  if (raw.team)  return normaliseTeams(raw.team);

  return [];
}

const Skeleton = ({ className = '' }: { className?: string }) => (
  <div className={`animate-pulse bg-gray-200 rounded ${className}`} />
);

function SkeletonStatCard({ rows = 3 }: { rows?: number }) {
  return (
    <div className="bg-white rounded-lg shadow-sm p-6 space-y-4">
      <Skeleton className="h-5 w-2/5" />
      <div className="grid grid-cols-3 gap-4">
        {Array.from({ length: rows }).map((_, i) => (
          <div key={i} className="space-y-2">
            <Skeleton className="h-8 w-3/4" />
            <Skeleton className="h-3 w-full" />
          </div>
        ))}
      </div>
      <Skeleton className="h-3 w-1/2" />
      <Skeleton className="h-3 w-2/5" />
    </div>
  );
}

function SkeletonFundingCard() {
  return (
    <div className="bg-white rounded-lg shadow-sm p-6 space-y-4">
      <Skeleton className="h-5 w-2/5" />
      <Skeleton className="h-3 w-1/3" />
      <Skeleton className="h-9 w-1/2" />
      <Skeleton className="h-3 w-3/4" />
      <Skeleton className="h-3 w-2/3" />
    </div>
  );
}

function SkeletonAiRingCard() {
  return (
    <div className="bg-white rounded-lg shadow-sm p-6 space-y-4 flex flex-col items-center">
      <Skeleton className="h-5 w-2/5 self-start" />
      <div className="w-32 h-32 rounded-full border-[12px] border-gray-200 animate-pulse" />
      <Skeleton className="h-3 w-3/4" />
      <Skeleton className="h-3 w-2/3" />
    </div>
  );
}

function SkeletonChartCard({ height = 300 }: { height?: number }) {
  return (
    <div className="bg-white rounded-lg shadow-sm p-4 space-y-4">
      <div className="flex items-center justify-between">
        <Skeleton className="h-5 w-1/3" />
        <Skeleton className="h-7 w-20" />
      </div>
      <div className="flex items-end gap-3 px-4" style={{ height }}>
        {[55, 80, 45, 70, 35].map((h, i) => (
          <div key={i} className="flex-1 flex flex-col items-center gap-2">
            <Skeleton className="w-full rounded" />
            <Skeleton className="h-3 w-3/4" />
          </div>
        ))}
      </div>
    </div>
  );
}

function SkeletonAiInsightCard() {
  return (
    <div className="rounded-xl p-6 mb-8 mt-8 bg-white space-y-4">
      <div className="flex items-center justify-between">
        <div className="space-y-2">
          <Skeleton className="h-6 w-36" />
          <Skeleton className="h-4 w-56" />
        </div>
      </div>
      {[1, 2, 3].map((i) => (
        <div key={i} className="p-4 bg-blue-50 rounded-lg flex items-start gap-3">
          <div className="mt-1 h-2 w-2 rounded-full bg-blue-200 flex-shrink-0 animate-pulse" />
          <div className="flex-1 space-y-2">
            <Skeleton className="h-3 w-full" />
            <Skeleton className="h-3 w-4/5" />
          </div>
        </div>
      ))}
    </div>
  );
}

function SkeletonDeadlineComplianceRow() {
  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
      {[1, 2].map((i) => (
        <div key={i} className="bg-white rounded-xl shadow-sm p-6 space-y-4">
          <div className="flex items-center gap-3 mb-2">
            <Skeleton className="h-8 w-8 rounded-full" />
            <Skeleton className="h-5 w-40" />
          </div>
          <Skeleton className="h-16 w-full rounded-lg" />
          <Skeleton className="h-16 w-full rounded-lg" />
        </div>
      ))}
    </div>
  );
}

function SkeletonTeamCard() {
  return (
    <div className="bg-white rounded-xl shadow-sm p-6 space-y-4">
      <Skeleton className="h-6 w-40" />
      <Skeleton className="h-4 w-32" />
      {[1, 2, 3].map((i) => (
        <div key={i} className="flex justify-between">
          <Skeleton className="h-4 w-1/3" />
          <Skeleton className="h-4 w-8" />
        </div>
      ))}
    </div>
  );
}

function TeamPerformanceSection({ teams }: { teams: Team[] }) {
  if (!teams.length) {
    return (
      <div className="bg-white rounded-xl shadow-sm p-6">
        <h3 className="text-xl font-bold text-gray-900 mb-4">Team Performance</h3>
        <p className="text-sm text-gray-400">No team data available</p>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6 mb-8">
      {teams.map((team) => {
        const sorted = [...team.appsPerMember].sort(
          (a, b) => b.totalApplications - a.totalApplications
        );
        const totalApps = sorted.reduce((s, m) => s + m.totalApplications, 0);

        return (
          <div key={team.teamId} className="bg-white rounded-xl shadow-sm p-4 flex flex-col gap-2">
            <div className="flex items-center justify-between w-full">
  <div className="flex items-center gap-2 min-w-0 flex-1">
    <span className="material-icons bg-primary rounded-full text-[16px] p-1.5 text-white shrink-0">
      group
    </span>

    <h3
      className="text-lg font-bold text-gray-900 truncate" 
      title={team.teamName}
    >
      {team.teamName}
    </h3>
  </div>

  <span className="text-xs font-semibold bg-gray-100 text-gray-600 rounded-full px-2.5 py-1 shrink-0 ml-3">
    {team.appsPerMember.length} member{team.appsPerMember.length !== 1 ? 's' : ''}
  </span>
</div>
            {/* <div className="flex items-center justify-between px-4 py-3 bg-blue-50 rounded-lg">
              <span className="text-sm font-medium text-gray-700">Total Applications</span>
              <span className="text-xl font-bold text-blue-600">{totalApps}</span>
            </div> */}

            {/* <div className="divide-y divide-gray-100">
              {sorted.length === 0 && (
                <p className="text-sm text-gray-400 py-2">No members found</p>
              )}
              {sorted.map((member, idx) => {
                const pct = totalApps > 0 ? Math.round((member.totalApplications / totalApps) * 100) : 0;
                const displayName =
                  member.name.length > 20 && !member.name.includes(' ')
                    ? member.name.slice(0, 8) + '…' + member.name.slice(-4)
                    : member.name;

                return (
                  <div key={member.name + idx} className="py-3">
                    <div className="flex items-center justify-between mb-1.5">
                      <span
                        className="text-sm text-gray-700 font-medium truncate max-w-[160px]"
                        title={member.name}
                      >
                        {displayName}
                      </span>
                      <div className="flex items-center gap-2 shrink-0 ml-2">
                        <span className="text-xs text-gray-400">{pct}%</span>
                        <span className="text-sm font-bold text-gray-900 w-6 text-right">
                          {member.totalApplications}
                        </span>
                      </div>
                    </div>
                    <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
                      <div
                        className="h-full bg-primary rounded-full transition-all duration-500"
                        style={{ width: `${pct}%` }}
                      />
                    </div>
                  </div>
                );
              })}
            </div> */}
          </div>
        );
      })}
    </div>
  );
}

const filterTypes = ['daily', 'weekly', 'monthly'];
const filterRanges = [
  { value: '30d', label: 'Last 30 Days' },
  { value: '60d', label: 'Last 60 Days' },
  { value: '90d', label: 'Last 90 Days' },
];
const STAGES = [
  { status: 'saved',       label: 'Saved' },
  { status: 'draft',       label: 'Draft' },
  { status: 'in_progress', label: 'In Progress' },
  { status: 'applied',     label: 'Applied' },
  { status: 'approved',    label: 'Approved' },
  { status: 'awarded',     label: 'Awarded' },
] as const;

const Report: React.FC = () => {

  const [showFilterModal, setShowFilterModal] = useState(false);
  const [useViewType,     setUseViewType]     = useState(false);
  const [useDateRange,    setUseDateRange]    = useState(false);
  const [filterType,      setFilterType]      = useState('');
  const [rangeFilter,     setRangeFilter]     = useState('');
  const [useCustomDate,   setUseCustomDate]   = useState(false);
  const [startDate,       setStartDate]       = useState('');
  const [endDate,         setEndDate]         = useState('');
  const [appliedFilters,  setAppliedFilters]  = useState<AppliedFilters | null>(null);

  const [statsLoading,    setStatsLoading]    = useState(true);
  const [pipelineLoading, setPipelineLoading] = useState(true);
  const [aiInsightLoading,setAiInsightLoading]= useState(true);
  const [bottomLoading,   setBottomLoading]   = useState(true);

  const [pipeline,   setPipeline]   = useState<ApplicationStats | undefined>();
  const [funding,    setFunding]    = useState<any>();
  const [compliance, setCompliance] = useState<{ docsUploaded: number; expiringSoon: number } | null>(null);
  const [teams,      setTeams]      = useState<Team[]>([]);
  const [deadlines,  setDeadlines]  = useState<{ upcomingDeadlines: any[]; missedDeadlinesCount: number; avgTimeToSubmit: number | null } | null>(null);
  const [aiReport,   setAIReport]   = useState<{ aiDraftsThisPeriod: number; aiDraftsTotal: number; aiUsagePercentage: number } | null>(null);
  const [aiInsight,  setAiInsight]  = useState<AiInsight | null>(null);

  useEffect(() => {
    const params: Record<string, any> = {};
    if (appliedFilters?.filterType)  params.filter = appliedFilters.filterType;
    if (appliedFilters?.rangeFilter) params.range  = appliedFilters.rangeFilter;
    if (appliedFilters?.startDate && appliedFilters?.endDate) {
      params.start = appliedFilters.startDate;
      params.end   = appliedFilters.endDate;
    }

    let alive = true;

    setStatsLoading(true);
    setPipelineLoading(true);
    setBottomLoading(true);

    (async () => {
      try {
        const [pipelines, fundingRes, aiRes] = await Promise.all([
          getPipeline(params),
          getFunding(params),
          getAIReport(params),
        ]);
        if (!alive) return;
        setPipeline(pipelines);
        setFunding(fundingRes);
        setAIReport(aiRes);
        setStatsLoading(false);
        setPipelineLoading(false);

        const [complianceRes, teamRes, deadlinesRes] = await Promise.all([
          getCompliance(params),
          getTeam(params),
          getDeadlines(params),
        ]);
        if (!alive) return;
        setCompliance(complianceRes);

        const rawTeam = teamRes?.teams ?? teamRes?.team ?? teamRes;
        setTeams(normaliseTeams(rawTeam));

        setDeadlines(deadlinesRes);
        setBottomLoading(false);

      } catch (e) {
        toast.error('Error loading report data');
        if (alive) { setStatsLoading(false); setPipelineLoading(false); setBottomLoading(false); }
      }
    })();

    return () => { alive = false; };
  }, [appliedFilters]);

  useEffect(() => {
    let isMounted = true;
    setAiInsightLoading(true);
    (async () => {
      try {
        const res = await getDashboardAiInsight();
        if (isMounted) setAiInsight(res);
      } catch { if (isMounted) setAiInsight(null); }
      finally   { if (isMounted) setAiInsightLoading(false); }
    })();
    return () => { isMounted = false; };
  }, []);

  const normalisedInsights = normaliseInsights(aiInsight?.insights);

  const handleApplyFilters = () => {
    const filters: AppliedFilters = {};
    if (useViewType && filterType) filters.filterType = filterType;
    if (useDateRange) {
      if (useCustomDate && startDate && endDate) { filters.startDate = startDate; filters.endDate = endDate; }
      else if (rangeFilter) filters.rangeFilter = rangeFilter;
    }
    setAppliedFilters(filters);
    setShowFilterModal(false);
  };

  const handleResetFilters = () => {
    setUseViewType(false); setUseDateRange(false); setFilterType(''); setRangeFilter('');
    setUseCustomDate(false); setStartDate(''); setEndDate(''); setAppliedFilters(null);
  };

  const handleViewTypeCheckbox = (checked: boolean) => {
    setUseViewType(checked);
    if (checked) { setUseDateRange(false); setRangeFilter(''); setUseCustomDate(false); setStartDate(''); setEndDate(''); }
    else setFilterType('');
  };

  const handleDateRangeCheckbox = (checked: boolean) => {
    setUseDateRange(checked);
    if (checked) { setUseViewType(false); setFilterType(''); }
    else { setRangeFilter(''); setUseCustomDate(false); setStartDate(''); setEndDate(''); }
  };

  const getCount = (status: keyof ApplicationStats['stageCounts']): number =>
    pipeline?.stageCounts?.[status] ?? 0;

  const getFilterDisplayText = () => {
    if (!appliedFilters) return null;
    const parts: string[] = [];
    if (appliedFilters.filterType) parts.push(appliedFilters.filterType.charAt(0).toUpperCase() + appliedFilters.filterType.slice(1));
    if (appliedFilters.startDate && appliedFilters.endDate) {
      parts.push(`${appliedFilters.startDate} to ${appliedFilters.endDate}`);
    } else if (appliedFilters.rangeFilter) {
      const map: Record<string, string> = { '30d': 'Last 30 Days', '60d': 'Last 60 Days', '90d': 'Last 90 Days' };
      parts.push(map[appliedFilters.rangeFilter] ?? appliedFilters.rangeFilter);
    }
    return parts.join(' | ');
  };

  const aiPercentage = Math.min(aiReport?.aiUsagePercentage ?? 0, 100);
  const radius = 56;
  const circumference = 2 * Math.PI * radius;
  const strokeDashoffset = circumference - (aiPercentage / 100) * circumference;

  return (
    <div className="min-h-screen bg-gray-50 p-8">

      <div className="flex justify-between items-start mb-8">
        <div>
          <h1 className="text-3xl font-bold mb-2 text-gray-800">Grant Reports</h1>
          <p className="text-gray-600">
            View detailed insights and analytics across all grant applications, funding stages, and outcomes.
          </p>
        </div>
        <div className="flex flex-col-reverse items-center gap-3">
          {appliedFilters && (
            <div className="flex items-center gap-2 bg-blue-50 border border-blue-200 rounded-lg px-4 py-2">
              <Calendar className="w-4 h-4 text-blue-600" />
              <span className="text-sm text-blue-900">{getFilterDisplayText()}</span>
              <button onClick={handleResetFilters} className="ml-2 text-blue-600 hover:text-blue-800">
                <X className="w-4 h-4" />
              </button>
            </div>
          )}
          <Button onClick={() => setShowFilterModal(true)}>Apply Filters</Button>
        </div>
      </div>

      {showFilterModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg mx-4">
            <div className="p-6 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <h2 className="text-2xl font-bold text-gray-800">Filter Reports</h2>
                <button onClick={() => setShowFilterModal(false)} className="text-gray-400 hover:text-gray-600">
                  <X className="w-6 h-6" />
                </button>
              </div>
            </div>

            <div className="p-6 space-y-6">
              <div>
                <div className="flex items-center gap-3 mb-3">
                  <input type="checkbox" id="useViewType" checked={useViewType}
                    onChange={(e) => handleViewTypeCheckbox(e.target.checked)} className="w-5 h-5 text-red-800 rounded" />
                  <label htmlFor="useViewType" className="text-sm font-semibold text-gray-700 cursor-pointer">Filter Type</label>
                </div>
                <div className="grid grid-cols-3 gap-3">
                  {filterTypes.map((type) => (
                    <button key={type} onClick={() => setFilterType(type)} disabled={!useViewType}
                      className={`py-3 px-4 rounded-lg font-medium transition-all ${
                        filterType === type && useViewType ? 'bg-primary text-white shadow-md'
                          : !useViewType ? 'bg-gray-50 text-gray-400 cursor-not-allowed'
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}>
                      {type.charAt(0).toUpperCase() + type.slice(1)}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <div className="flex items-center gap-3 mb-3">
                  <input type="checkbox" id="useDateRange" checked={useDateRange}
                    onChange={(e) => handleDateRangeCheckbox(e.target.checked)}
                    className="w-5 h-5 text-blue-600 rounded focus:ring-2 focus:ring-blue-500" />
                  <label htmlFor="useDateRange" className="text-sm font-semibold text-gray-700 cursor-pointer">Date Range</label>
                </div>
                <div className="flex gap-2 mb-4">
                  {(['preset', 'custom'] as const).map((mode) => (
                    <button key={mode} onClick={() => { setUseCustomDate(mode === 'custom'); if (mode === 'preset') setRangeFilter(''); }}
                      disabled={!useDateRange}
                      className={`flex-1 py-2 px-4 rounded-lg font-medium transition-all border-2 ${
                        (mode === 'custom' ? useCustomDate : !useCustomDate) && useDateRange
                          ? 'bg-primary text-white border-black'
                          : !useDateRange ? 'bg-gray-50 text-gray-400 border-gray-200 cursor-not-allowed'
                          : 'bg-gray-50 text-gray-600 border-gray-200'
                      }`}>
                      {mode === 'preset' ? 'Preset Range' : 'Custom Dates'}
                    </button>
                  ))}
                </div>

                {!useCustomDate && (
                  <div className="grid grid-cols-3 gap-3">
                    {filterRanges.map((range) => (
                      <button key={range.value} onClick={() => setRangeFilter(range.value)} disabled={!useDateRange}
                        className={`py-3 px-4 rounded-lg font-medium transition-all ${
                          rangeFilter === range.value && useDateRange ? 'bg-primary text-white shadow-md'
                            : !useDateRange ? 'bg-gray-50 text-gray-400 cursor-not-allowed'
                            : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                        }`}>
                        {range.label}
                      </button>
                    ))}
                  </div>
                )}

                {useCustomDate && (
                  <div className="space-y-4">
                    {(['start', 'end'] as const).map((which) => (
                      <div key={which}>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          {which === 'start' ? 'Start Date' : 'End Date'}
                        </label>
                        <input type="date" disabled={!useDateRange}
                          value={which === 'start' ? startDate : endDate}
                          min={which === 'end' ? startDate : undefined}
                          onChange={(e) => which === 'start' ? setStartDate(e.target.value) : setEndDate(e.target.value)}
                          className={`w-full px-4 py-2 border border-gray-300 rounded-lg ${
                            !useDateRange ? 'bg-gray-50 text-gray-400 cursor-not-allowed' : 'focus:ring-2 focus:ring-blue-500'
                          }`} />
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            <div className="p-6 border-t border-gray-200 flex gap-3">
              <Button onClick={() => setShowFilterModal(false)} className="flex-1 hover:bg-black bg-gray-700">Cancel</Button>
              <Button onClick={handleApplyFilters}
                disabled={
                  (!useViewType && !useDateRange) ||
                  (useViewType && !filterType) ||
                  (useDateRange && useCustomDate && (!startDate || !endDate)) ||
                  (useDateRange && !useCustomDate && !rangeFilter)
                }
                className={`flex-1 ${
                  (!useViewType && !useDateRange) ||
                  (useViewType && !filterType) ||
                  (useDateRange && useCustomDate && (!startDate || !endDate)) ||
                  (useDateRange && !useCustomDate && !rangeFilter)
                    ? 'bg-gray-300 text-gray-500 cursor-not-allowed' : 'text-white'
                }`}>
                Apply Filters
              </Button>
            </div>
          </div>
        </div>
      )}

      {statsLoading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
          <SkeletonStatCard rows={6} />
          <SkeletonFundingCard />
          <SkeletonAiRingCard />
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">

          <div className="bg-white rounded-lg shadow-sm p-6">
            <h3 className="text-lg font-semibold mb-4">Pipeline Summary</h3>
            <div className="grid grid-cols-3 gap-4 mb-4">
              {STAGES.map(({ status, label }) => (
                <div key={status}>
                  <div className="text-3xl font-bold">{getCount(status as keyof ApplicationStats['stageCounts'])}</div>
                  <div className="text-sm text-gray-600">{label}</div>
                </div>
              ))}
            </div>
            <div className="text-sm text-gray-600 mt-4">
              <p>Draft → Applied: <span className="font-semibold">{(pipeline?.conversion.draft_to_submitted ?? 0).toFixed(2)}%</span></p>
              <p>Applied → Success: <span className="font-semibold">{(pipeline?.conversion.submitted_to_success ?? 0).toFixed(2)}%</span></p>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-sm p-6">
            <h3 className="text-lg font-semibold mb-4">Funding Performance</h3>
            <div className="mb-4">
              <div className="text-sm text-gray-600 mb-1">Total Funding</div>
              <div className="text-3xl font-bold">£{funding?.totalFunding ?? 0}</div>
            </div>
            <div className="space-y-2 text-sm">
              <div className="text-gray-600">Avg Award: <span className="font-semibold">£{funding?.avgAward ?? 0}</span></div>
              <div className="text-gray-600">Success Rate: <span className="font-semibold">{Math.round(funding?.successRate ?? 0)}%</span></div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-sm p-6">
            <h3 className="text-lg font-semibold mb-4">AI Usage</h3>
            <div className="relative w-32 h-32 flex mx-auto">
              <svg className="transform -rotate-90 w-32 h-32">
                <circle cx="64" cy="64" r="56" stroke="#e5e7eb" strokeWidth="12" fill="none" />
                <circle cx="64" cy="64" r="56" stroke="#3b82f6" strokeWidth="12" fill="none"
                  strokeLinecap="round" strokeDasharray={circumference} strokeDashoffset={strokeDashoffset}
                  style={{ transition: 'stroke-dashoffset 0.6s ease' }} />
              </svg>
              <div className="absolute inset-0 flex items-center justify-center">
                <span className="text-3xl font-bold">{aiReport?.aiUsagePercentage ?? 0}%</span>
              </div>
            </div>
            <div className="space-y-2 text-sm mt-4">
              <div className="text-gray-600">AI Drafts (This Month): <span className="font-semibold">{aiReport?.aiDraftsThisPeriod ?? 0}</span></div>
              <div className="text-gray-600">AI Drafts (Total): <span className="font-semibold">{aiReport?.aiDraftsTotal ?? 0}</span></div>
            </div>
          </div>
        </div>
      )}

      {pipelineLoading
        ? <SkeletonChartCard height={300} />
        : <GrantPipelineHealthCard pipeline={pipeline} />
      }

      {aiInsightLoading ? (
        <SkeletonAiInsightCard />
      ) : (
        <div className="rounded-xl p-6 mb-8 mt-8 bg-white">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="text-xl font-bold mb-1">AI Insights</h3>
              <p className="text-gray-600">{aiInsight?.greeting ?? 'AI-powered grant guidance'}</p>
            </div>
          </div>

          {normalisedInsights.length > 0 ? (
            <div className="space-y-3 mt-4">
              {normalisedInsights.map((item, idx) => (
                <div key={idx} className="p-4 bg-blue-50 rounded-lg flex items-start gap-3">
                  <span className="mt-[6px] h-2 w-2 rounded-full bg-blue-400 flex-shrink-0" />
                  <p className="text-sm text-gray-800 leading-snug">
                    {item.label && <span className="font-semibold text-gray-900">{item.label}: </span>}
                    {item.text}
                  </p>
                </div>
              ))}
            </div>
          ) : (
            <div className="p-6 bg-gray-50 rounded-lg text-center">
              <p className="text-gray-500">No AI insights available</p>
              <p className="text-sm text-gray-400 mt-1">Insights will appear as you use the platform</p>
            </div>
          )}

          {!!aiInsight?.next_steps?.length && (
            <div className="mt-4">
              <p className="text-sm font-semibold text-gray-600 uppercase tracking-wide mb-2">Suggested next steps</p>
              <ol className="list-decimal ml-5 space-y-1">
                {aiInsight.next_steps.map((step, i) => (
                  <li key={i} className="text-sm text-gray-700 leading-snug">
                    {typeof step === 'string' ? step : (step as any)?.title ?? JSON.stringify(step)}
                  </li>
                ))}
              </ol>
            </div>
          )}

          {aiInsight?.positive_message && (
            <div className="mt-4 p-4 bg-green-50 rounded-lg text-green-800 font-medium text-sm">
              {aiInsight.positive_message}
            </div>
          )}
        </div>
      )}

      {bottomLoading ? (
        <>
          <SkeletonDeadlineComplianceRow />
          <SkeletonTeamCard />
        </>
      ) : (
        <>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
            <div className="bg-white rounded-xl shadow-sm p-6">
              <div className="flex items-center gap-3 mb-6">
                <span className="material-icons bg-primary rounded-full text-[18px] p-[6px]">access_time</span>
                <h3 className="text-xl font-bold text-gray-900">Deadlines Overview</h3>
              </div>
              <div className="space-y-4">
                <div className="flex items-center justify-between p-4 bg-orange-50 rounded-lg">
                  <div className="flex items-center gap-3">
                    <span className="material-icons">error_outline</span>
                    <span className="font-medium text-gray-900">Missed Deadlines</span>
                  </div>
                  <span className="text-2xl font-bold text-orange-600">{deadlines?.missedDeadlinesCount ?? 0}</span>
                </div>
                <div className="flex items-center justify-between p-4 bg-blue-50 rounded-lg">
                  <div className="flex items-center gap-3">
                    <span className="material-icons">access_time</span>
                    <span className="font-medium text-gray-900">Upcoming Deadlines</span>
                  </div>
                  <span className="text-2xl font-bold text-blue-600">{deadlines?.upcomingDeadlines?.length ?? 0}</span>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-xl shadow-sm p-6">
              <div className="flex items-center gap-3 mb-6">
                <span className="material-icons bg-primary rounded-full text-[18px] p-[6px]">check_circle</span>
                <h3 className="text-xl font-bold text-gray-900">Compliance Vault</h3>
              </div>
              <div className="space-y-4">
                <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                  <span className="text-sm font-medium text-gray-700">Documents Uploaded</span>
                  <span className="text-xl font-bold text-gray-900">{compliance?.docsUploaded ?? 0}</span>
                </div>
                <div className="flex items-center justify-between p-4 bg-yellow-50 rounded-lg">
                  <span className="text-sm font-medium text-gray-700">Expiring Soon</span>
                  <span className="text-xl font-bold text-yellow-600">{compliance?.expiringSoon ?? 0}</span>
                </div>
              </div>
            </div>
          </div>

          <div className="mb-2">
            <div className="flex items-center gap-3 mb-4">
              <span className="material-icons bg-primary rounded-full text-[18px] p-[6px] text-white">groups</span>
              <h3 className="text-xl font-bold text-gray-900">Team Performance</h3>
              {teams.length > 0 && (
                <span className="text-sm text-gray-500 font-medium">
                  {teams.length} team{teams.length !== 1 ? 's' : ''}
                </span>
              )}
            </div>
            <TeamPerformanceSection teams={teams} />
          </div>
        </>
      )}

    </div>
  );
};

export default Report;